import { API_ORIGIN } from "@/lib/apiOrigin";
import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Phone, Receipt, ShieldCheck, X, FileText, Navigation, CreditCard, ExternalLink, Loader2, AlertTriangle, Copy, Package, Share2, Truck } from "lucide-react";
import { api, formatApiError, tokenStore } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import RatingForm from "@/components/RatingForm";
import RatingDisplay from "@/components/RatingDisplay";
import DeliveryPhotoView from "@/components/DeliveryPhotoView";
import CustomerLiveActionCard from "@/components/CustomerLiveActionCard";
import OrderTrackingMap from "@/components/OrderTrackingMap";
import TrackingETACard from "@/components/TrackingETACard";
import OrderShareTrackingCard from "@/components/OrderShareTrackingCard";
import ShareTrackingModal from "@/components/ShareTrackingModal";
import CO2SavingsCard from "@/components/CO2SavingsCard";
import OrderTimelineCard from "@/components/OrderTimelineCard";
import GpsFreshnessBadge from "@/components/GpsFreshnessBadge";
import SubscriptionBreakdown from "@/components/SubscriptionBreakdown";
import CustomerTaxBreakdown from "@/components/CustomerTaxBreakdown";
import { validPriceTax } from "@/lib/priceTax";
import AutoRefreshIndicator from "@/components/AutoRefreshIndicator";
import CancelOrderModal from "@/components/CancelOrderModal";
import { TRACKING_ACTIVE_STATUSES } from "@/lib/useDriverLocationBroadcaster";
import { formatCHF, formatKm, formatDateTime, SIZE_LABEL, DELIVERY_TYPE_LABEL, STATUS_STEPS, STATUS_LABEL } from "@/lib/format";
import { formatAddress } from "@/lib/formatAddress";
import ParcelLabelCard from "@/components/ParcelLabelCard";
import { useCheckoutReturn } from "@/lib/useCheckoutReturn";
import { orderPaymentBlock } from "@/lib/orderPaymentStatus";
import CheckoutVerification from "@/components/CheckoutVerification";
import { downloadPdf } from "@/lib/downloadPdf";
import { refundPresentation } from "@/lib/orderRefund";

const BASE = API_ORIGIN;

const PAYMENT_PILL = {
  paid: { label: "Pagato", cls: "bg-emerald-50 text-emerald-700" },
  pending: { label: "In attesa di pagamento", cls: "bg-amber-50 text-amber-700" },
  expired: { label: "Sessione scaduta", cls: "bg-red-50 text-red-700" },
  failed: { label: "Pagamento fallito", cls: "bg-red-50 text-red-700" },
  cancelled: { label: "Pagamento annullato", cls: "bg-zinc-100 text-dapnow-muted" },
};

function Timeline({ status }) {
  const cancelled = status === "cancelled";
  const idx = cancelled ? -1 : STATUS_STEPS.indexOf(status);
  return (
    <ol className="space-y-3">
      {STATUS_STEPS.map((s, i) => {
        const reached = !cancelled && i <= idx;
        const current = !cancelled && i === idx;
        return (
          <li key={s} className="flex items-center gap-3">
            <span className={`h-3 w-3 rounded-full ${reached ? "bg-dapnow-blue" : "bg-dapnow-line"} ${current ? "ring-4 ring-dapnow-blue/20" : ""}`} />
            <span className={`text-sm ${reached ? "font-semibold text-dapnow-ink" : "text-dapnow-muted"}`}>{STATUS_LABEL[s]}</span>
          </li>
        );
      })}
      {cancelled && (
        <li className="flex items-center gap-3">
          <span className="h-3 w-3 rounded-full bg-dapnow-red ring-4 ring-red-500/20" />
          <span className="text-sm font-semibold text-dapnow-red">Annullata</span>
        </li>
      )}
    </ol>
  );
}

export default function CustomerOrderDetail() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [ratings, setRatings] = useState([]);
  const [, setInvoice] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [paying, setPaying] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (manual = false) => {
    if (manual) setRefreshing(true);
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      setLastUpdated(new Date());
      try {
        const r = await api.get(`/ratings/order/${id}`);
        setRatings(r.data);
      } catch { /* ratings optional */ }
    } catch (e) {
      if (manual) toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
      if (manual) setRefreshing(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Phase 43.6 + 43.16 — auto-refresh ordine ogni 15s per TUTTI gli stati attivi
  // (pre-tracking: pending/accepted) + (tracking attivo: picking_up, picked_up,
  // delivering) + (final: delivered) per cogliere foto consegna, payout, rating, etc.
  // Bugfix 43.16: prima saltava il refresh durante i tracking states quindi la
  // Timeline restava ferma su "In consegna" anche quando lo stato passava a delivered.
  useEffect(() => {
    if (!order) return;
    const refreshStates = new Set([
      "pending", "accepted", "picking_up", "picked_up", "delivering", "delivered",
    ]);
    if (!refreshStates.has(order.status)) return;
    const t = setInterval(() => { load(false); }, 15_000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.status, id]);

  // Memoize OrderTrackingMap props to avoid creating new objects every render
  // and prevent OrderTrackingMap's useEffect (which has these as deps) from firing
  // on every parent re-render — this caused fitBounds/marker churn.
  const mapPickup = useMemo(() => (
    tracking?.pickup ? { lat: tracking.pickup.lat, lng: tracking.pickup.lng }
      : (order?.pickup_lat ? { lat: order.pickup_lat, lng: order.pickup_lng } : null)
  ), [tracking?.pickup, order?.pickup_lat, order?.pickup_lng]);
  const mapDelivery = useMemo(() => (
    tracking?.delivery ? { lat: tracking.delivery.lat, lng: tracking.delivery.lng }
      : (order?.delivery_lat ? { lat: order.delivery_lat, lng: order.delivery_lng } : null)
  ), [tracking?.delivery, order?.delivery_lat, order?.delivery_lng]);
  const mapDriver = useMemo(() => (
    tracking?.driver?.last_location
      ? { lat: tracking.driver.last_location.lat, lng: tracking.driver.last_location.lng, heading: tracking.driver.last_location.heading }
      : null
  ), [tracking?.driver?.last_location]);
  const mapTrail = useMemo(() => tracking?.polyline || [], [tracking?.polyline]);

  // Live tracking polling — only when order is in active tracking state
  useEffect(() => {
    if (!order) return;
    let cancelled = false;
    const fetchTracking = async () => {
      try {
        const { data } = await api.get(`/orders/${id}/tracking`);
        if (!cancelled) setTracking(data);
      } catch { /* silent */ }
    };
    fetchTracking();
    if (TRACKING_ACTIVE_STATUSES.has(order.status)) {
      const t = setInterval(fetchTracking, 5_000);
      return () => { cancelled = true; clearInterval(t); };
    }
    return () => { cancelled = true; };
  }, [order?.status, id]);

  const generateInvoice = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/invoices/generate/${id}`);
      setInvoice(data);
      toast.success(`Fattura ${data.number} generata`);
      await downloadPdf(`${BASE}/api/invoices/${data.id}/pdf`, `DapNow-${data.number}.pdf`, { Authorization: `Bearer ${tokenStore.get()}` });
    } catch (e) {
      toast.error(e.response?.data?.detail ? formatApiError(e.response.data.detail) : e.message);
    } finally {
      setBusy(false);
    }
  };

  // Phase 43.11 — Cancel ora avviene tramite modale dedicata con preview rimborso.
  const [cancelOpen, setCancelOpen] = useState(false);

  // Stripe — clic "Paga ora" o link mancante
  const payNow = async () => {
    setPaying(true);
    try {
      if (orderPaymentBlock(order)) { toast.info(orderPaymentBlock(order).message); return; }
      if (order?.payment_status === "paid") { toast.info("Ordine già pagato"); return; }
      // The server verifies/reuses an open session or reconciles a completed one.
      const { data } = await api.post(`/orders/${id}/payment/checkout-session`);
      if (data.already_paid) { toast.info("Ordine già pagato"); await load(); return; }
      const url = data.url;
      if (!url) { toast.error("Impossibile creare la sessione di pagamento"); return; }
      window.location.href = url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setPaying(false);
    }
  };

  const sessionId = params.get("session_id");
  const verification = useCheckoutReturn({
    sessionId: sessionId ? `${id}:${sessionId}` : null,
    request: async () => {
      const refreshed = (await api.post(`/orders/${id}/payment/refresh`, { session_id: sessionId })).data;
      const latest = (await api.get(`/orders/${id}`)).data;
      return { ...refreshed, ...latest };
    },
    isComplete: data => data.payment_status === "paid",
    getBlock: orderPaymentBlock,
    isTerminal: data => ["expired", "failed", "cancelled"].includes(data.payment_status),
    onComplete: () => {
      toast.success("Pagamento ricevuto. Ordine confermato!");
      setOrder(previous => previous ? { ...previous, payment_status: "paid" } : previous);
      load();
      setParams(previous => {
        const next = new URLSearchParams(previous);
        next.delete("session_id");
        next.delete("cancelled");
        return next;
      }, { replace: true });
    },
    maxAttempts: 6,
  });
  const pollingPayment = verification.status === "checking";

  const pay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${id}/pay`);
      setOrder(data);
      toast.success("Pagamento confermato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div className="card-soft p-10 text-center text-dapnow-muted">Caricamento…</div>;
  if (!order) return <div className="card-soft p-10 text-center text-dapnow-muted">Ordine non trovato.</div>;

  // Phase 43.11 — annullamento permesso fino a "picking_up". Dopo (picked_up,
  // delivering, delivered) il cliente NON può più annullare autonomamente.
  const canCancel = ["pending", "accepted", "picking_up"].includes(order.status);
  const postPickupNeedSupport = ["picked_up", "delivering"].includes(order.status);
  const ps = order.payment_status || "pending";
  const payPill = PAYMENT_PILL[ps] || PAYMENT_PILL.pending;
  const paymentBlock = orderPaymentBlock(order) || orderPaymentBlock(verification.data);
  const showPayCard = ps !== "paid" && !paymentBlock && !verification.unresolved;

  return (
    <div className="space-y-5 overflow-x-hidden" data-testid="customer-order-detail">
      <Link to="/app/orders" className="inline-flex items-center gap-1.5 text-sm font-semibold text-dapnow-muted hover:text-dapnow-ink">
        <ArrowLeft className="h-4 w-4" /> Tutti gli ordini
      </Link>

      <CheckoutVerification verification={paymentBlock || verification} testId="order-payment-verification" />

      {/* Phase 43.51 — Blocco "Stato attuale" live, sempre in cima */}
      {!paymentBlock && <CustomerLiveActionCard
        order={order}
        tracking={tracking}
        onPay={showPayCard ? payNow : null}
        shareTrackingUrl={order.public_tracking_url || (order.share_token ? `${BASE}/track/${order.share_token}` : null)}
      />}

      {/* Phase 44.22 — Card "Invia il link al destinatario" prominente nella
          schermata ordine (richiesta utente: stesso design del profilo).
          Visibile per tutti gli ordini attivi (no senso condividere tracking
          per ordini cancellati/consegnati/scaduti). */}
      {!paymentBlock && !["delivered", "cancelled", "refunded", "expired"].includes(order.status) && (
        <OrderShareTrackingCard orderId={order.id} testid="customer-order-share-card" />
      )}

      {/* Phase 44.07 — Rating immediato sotto il blocco "Consegnato"
          (no scroll). Visibile solo se non già valutato. */}
      {order.status === "delivered" && !ratings.find((r) => r.from_role === "customer") && (
        <section
          className="card-soft p-5 sm:p-6 border-l-4 border-l-emerald-500 bg-emerald-50/30"
          data-testid="delivered-rating-inline"
        >
          <div className="flex items-start gap-3">
            <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-100 text-emerald-700 shrink-0">
              <ShieldCheck className="h-5 w-5" />
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-display text-base font-bold tracking-tight text-emerald-900">
                Consegna completata
              </p>
              <p className="text-xs text-emerald-800 mt-0.5">
                Lascia una valutazione del driver — bastano 5 secondi.
              </p>
            </div>
          </div>
          <div className="mt-4">
            <RatingForm orderId={order.id} otherName={order.driver_name} role="customer" onDone={load} />
          </div>
        </section>
      )}

      {/* Phase 44.26 — Card "Identificazione pacco" SPOSTATA in alto.
          Era in fondo (passava in secondo piano per l'utente). Ora subito
          dopo lo stato live + share-card, prima della timeline: massima
          visibilità per la fase pre-ritiro.
          Phase 44.27 — Mostrata anche post-pickup (fino a delivered/cancelled
          esclusi) perché l'utente vuole poterla recuperare in qualsiasi
          momento durante la consegna ("cosi uno se vuole recuperarle lo fa"). */}
      {!paymentBlock && !["delivered", "cancelled", "refunded", "expired"].includes(order.status) && (
        <ParcelLabelCard orderId={order.id} testid="order-number-banner" />
      )}

      {/* Phase 44.08 — Timeline ordine (auto-open in-progress, collapsed on delivered/cancelled) */}
      <OrderTimelineCard
        order={order}
        ratings={ratings}
      />

      {/* Phase 44.26 — Banner numero ordine spostato in alto (vedi ParcelLabelCard
          subito dopo lo stato live). Mantenuta sola la sezione Pagamento qui sotto. */}

      {/* Payment status card */}
      {showPayCard && (
        <section
          className={`card-soft p-5 sm:p-6 border-l-4 ${ps === "expired" || ps === "failed" ? "border-l-dapnow-red bg-red-50/40" : "border-l-amber-400 bg-amber-50/40"}`}
          data-testid="payment-card"
        >
          <div className="flex items-start gap-3 flex-wrap">
            <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${ps === "expired" || ps === "failed" ? "bg-red-100 text-dapnow-red" : "bg-amber-100 text-amber-700"}`}>
              {ps === "expired" || ps === "failed" ? <AlertTriangle className="h-5 w-5" /> : <CreditCard className="h-5 w-5" />}
            </span>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-display text-lg font-bold tracking-tight">Pagamento</h2>
                <span className={`pill text-[10px] ${payPill.cls}`} data-testid="payment-status-pill">{payPill.label}</span>
                {order.payment_simulated && (
                  <span className="pill text-[10px] bg-zinc-100 text-dapnow-muted" data-testid="payment-simulated-pill">Simulato</span>
                )}
              </div>
              <p className="text-sm text-dapnow-muted mt-0.5">
                {ps === "expired" || ps === "failed"
                  ? "La sessione precedente è scaduta o il pagamento è fallito. Riprova per confermare l'ordine."
                  : pollingPayment
                    ? "Stiamo verificando lo stato del pagamento appena effettuato…"
                    : "Per confermare la consegna devi completare il pagamento. Importo: "}
                {!(ps === "expired" || ps === "failed") && !pollingPayment && (
                  <strong className="text-dapnow-ink">{formatCHF(order.final_price ?? order.price ?? order.gross_amount)}</strong>
                )}
              </p>
            </div>
            <button
              type="button"
              onClick={payNow}
              disabled={paying || pollingPayment}
              data-testid="pay-now-btn"
              className="btn-primary"
            >
              {paying ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Apertura…</>
                : pollingPayment ? <><Loader2 className="h-4 w-4 animate-spin mr-1.5" /> Verifica…</>
                : <><CreditCard className="h-4 w-4 mr-1.5" /> Paga ora</>}
            </button>
          </div>
        </section>
      )}

      {/* Phase 44.25 — Rimosso "Pagamento ricevuto" inline banner: ridondante
          con la grid "Pagamento Pagato · CHF X" già visibile sotto la mappa,
          e con l'evento "Pagamento" nella OrderTimelineCard. */}

      {ps === "paid" && (order.delivery_type || order.urgency) === "shared" && (
        <CO2SavingsCard
          distance_km={order.distance_km}
          delivery_type={order.delivery_type || order.urgency}
        />
      )}

      {/* Phase 43.18d — Card rimborso (solo se ordine cancellato + era pagato) */}
      {order.status === "cancelled" && order.payment_status === "paid" && order.refund_status && order.refund_status !== "not_applicable" && (
        <RefundStatusCard order={order} />
      )}

      {/* Phase 44.25 — Rimossi banner "Stiamo cercando un driver" e
          "Driver assegnato — ritiro in preparazione": entrambi ridondanti
          con la CustomerLiveActionCard in cima che già mostra in grande il
          messaggio contestuale ("Cerchiamo un driver" / "Driver assegnato"). */}

      <div className="card-soft overflow-hidden">
        {/* Phase 43.14 + 43.15 — Driver-nearby banner (ETA ≤ 5 min, idempotente per fase)
            + dettagli veicolo (marca / modello / colore / targa). */}
        {tracking?.proximity_alert && (
          <div
            data-testid="proximity-alert-banner"
            data-proximity-phase={tracking.proximity_alert.phase}
            className="px-4 sm:px-5 py-3 border-b border-amber-200 bg-amber-50 text-amber-900 flex items-start gap-3"
          >
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-amber-100 text-amber-700 flex-shrink-0">
              <Truck className="h-4 w-4" />
            </span>
            <div className="flex-1 text-sm">
              <p className="font-semibold tracking-tight">
                {tracking.proximity_alert.phase === "to_pickup"
                  ? "Il driver è quasi arrivato al ritiro"
                  : "Il driver è quasi arrivato alla consegna"}
              </p>
              {tracking.proximity_alert.driver_card?.summary && (
                <p
                  data-testid="proximity-alert-vehicle"
                  className="text-amber-900 text-xs mt-1 font-semibold"
                >
                  {tracking.proximity_alert.driver_card.first_name
                    ? `${tracking.proximity_alert.driver_card.first_name} · `
                    : ""}
                  {tracking.proximity_alert.driver_card.summary}
                </p>
              )}
              <p className="text-amber-900/80 text-xs mt-0.5">
                {tracking.proximity_alert.phase === "to_pickup"
                  ? "Tieni pronto il pacco e resta raggiungibile al telefono."
                  : "Preparati a ricevere il pacco."}{" "}
                Stima ~{Math.round(tracking.proximity_alert.eta_min)} min.
              </p>
            </div>
          </div>
        )}
        <OrderTrackingMap
          pickup={mapPickup}
          delivery={mapDelivery}
          driver={mapDriver}
          trail={mapTrail}
          phase={
            // pre-pickup: focus driver↔ritiro · post-pickup: focus driver↔consegna
            ["accepted", "picking_up"].includes(order?.status) ? "to_pickup"
              : ["picked_up", "delivering"].includes(order?.status) ? "to_delivery"
              : null
          }
          className="h-56 sm:h-72 w-full"
        />
        {tracking?.tracking_active && (
          <div className="px-3 pt-3 sm:px-5 sm:pt-4">
            <GpsFreshnessBadge
              trackingActive={tracking.tracking_active}
              lastLocationAtIso={tracking?.driver?.last_location_at}
            />
          </div>
        )}
        {(tracking?.tracking_active || tracking?.phase === "delivered") && (
          <TrackingETACard tracking={tracking} status={order.status} />
        )}
        <div className="p-5 sm:p-7">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="overline">Ordine</p>
              <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1">#{order.id.slice(0, 8).toUpperCase()}</h1>
              <p className="text-sm text-dapnow-muted">{formatDateTime(order.created_at)}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <StatusBadge order={order} audience="customer" />
              {["pending", "accepted"].includes(order.status) && (
                <AutoRefreshIndicator
                  lastUpdated={lastUpdated}
                  intervalSec={15}
                  loading={refreshing}
                  onRefresh={() => load(true)}
                />
              )}
            </div>
          </div>

          <div className="mt-5 grid sm:grid-cols-3 gap-3">
            <div className="rounded-2xl bg-dapnow-bg p-4">
              <p className="overline">Distanza</p>
              <p className="font-display text-xl font-bold tracking-tight mt-1">{formatKm(order.distance_km)}</p>
            </div>
            <div className="rounded-2xl bg-dapnow-bg p-4">
              <p className="overline">Pagamento</p>
              <p className="font-display text-xl font-bold tracking-tight mt-1 capitalize">{order.payment_status === "paid" ? "Pagato" : "Da pagare"}</p>
            </div>
            <div className="rounded-2xl bg-dapnow-blue text-white p-4">
              <p className="overline text-white/80">Totale</p>
              <p className="font-display text-2xl font-extrabold tracking-tight mt-1">{formatCHF(order.final_price ?? order.price)}</p>
            </div>
          </div>

          {!order.included_delivery_consumed && validPriceTax(order.charge_tax) && (
            <div className="mt-4 rounded-2xl bg-dapnow-bg p-4">
              <CustomerTaxBreakdown tax={order.charge_tax} title="Riepilogo della consegna" totalLabel="Totale prima del wallet" testId="order-detail-tax" />
              {order.credit_balance_applied_chf > 0 && <p className="mt-2 text-xs" data-testid="order-detail-wallet">Credito wallet utilizzato: {formatCHF(order.credit_balance_applied_chf)}</p>}
            </div>
          )}
          {order.included_delivery_consumed && (
            <div className="mt-4">
              <SubscriptionBreakdown
                data={{ ...order, final_price: order.final_price ?? order.price }}
                variant="inline"
              />
            </div>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-[1.4fr_1fr] gap-5">
        <div className="card-soft p-5 sm:p-6 space-y-5">
          <h2 className="font-display text-lg font-bold tracking-tight">Indirizzi</h2>
          <div className="space-y-3 text-sm">
            <div>
              <p className="overline text-[10px]">Ritiro</p>
              <p className="font-medium">{formatAddress(order.pickup_address)}</p>
              {order.pickup_contact_name && <p className="text-dapnow-muted">{order.pickup_contact_name} {order.pickup_contact_phone ? `· ${order.pickup_contact_phone}` : ""}</p>}
            </div>
            <div>
              <p className="overline text-[10px]">Consegna</p>
              <p className="font-medium">{formatAddress(order.delivery_address)}</p>
              {order.delivery_contact_name && <p className="text-dapnow-muted">{order.delivery_contact_name} {order.delivery_contact_phone ? `· ${order.delivery_contact_phone}` : ""}</p>}
            </div>
          </div>

          <div className="border-t border-dapnow-line pt-4">
            <h3 className="font-display text-lg font-bold tracking-tight">Pacco</h3>
            <p className="text-sm mt-1">{order.package_description}</p>
            <p className="text-xs text-dapnow-muted mt-1">{SIZE_LABEL[order.package_size || order.size]} · {DELIVERY_TYPE_LABEL[order.delivery_type || order.urgency]}</p>
            {order.notes && <p className="text-xs text-dapnow-muted mt-2">Note: {order.notes}</p>}
            {/* Phase 43.9 — Nota informativa elegante: tempi/percorso indicativi */}
            <p
              className="text-[11px] text-dapnow-muted/90 italic mt-3 leading-relaxed"
              data-testid="order-disclaimer-note"
            >
              <span className="font-bold not-italic">*</span> I tempi e il percorso mostrati sono indicativi e possono variare in base alla disponibilità del driver, al traffico e alle condizioni operative. La posizione del driver si aggiorna quando il segnale GPS è disponibile.
            </p>
          </div>

          {order.driver_name && (
            <div className="border-t border-dapnow-line pt-4 flex items-center justify-between">
              <div>
                <p className="overline text-[10px]">Trasportatore</p>
                <p className="font-display text-base font-semibold tracking-tight">{order.driver_name}</p>
              </div>
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue"><Phone className="h-4 w-4" /></span>
            </div>
          )}
        </div>

        <div className="space-y-5">
          {/* Phase 44.25 — Card "Tracking" con pallini rimossa: era ridondante
              con OrderTimelineCard (sezione "Cronologia ordine" più informativa,
              mostra date+azioni). Manteniamo solo la timeline come fonte unica
              di verità per l'avanzamento ordine. */}

          <div className="card-soft p-5 sm:p-6 space-y-3">
            {showPayCard && (
              <button data-testid="customer-pay-btn" onClick={payNow} disabled={busy || paying} className="btn-primary w-full">
                <Receipt className="h-4 w-4 mr-1.5" />
                {paying ? "Apro Stripe…" : `Paga ${formatCHF(order.final_price ?? order.price)}`}
              </button>
            )}
            {order.payment_status === "paid" && !paymentBlock && (
              <div className="flex items-center gap-2 text-sm text-emerald-700">
                <ShieldCheck className="h-4 w-4" /> Pagamento confermato
              </div>
            )}
            {order.payment_status === "paid" && paymentBlock?.status !== "review" && (
              <button data-testid="customer-invoice-btn" onClick={generateInvoice} disabled={busy} className="btn-ghost w-full">
                <FileText className="h-4 w-4 mr-1.5" /> Scarica fattura PDF
              </button>
            )}
            {canCancel && (
              <button
                data-testid="customer-cancel-btn"
                onClick={() => setCancelOpen(true)}
                disabled={busy}
                className="btn-ghost w-full text-dapnow-red border-dapnow-red/40"
              >
                <X className="h-4 w-4 mr-1.5" /> Annulla ordine
              </button>
            )}
            {postPickupNeedSupport && (
              <a
                href="mailto:support@dap-now.ch?subject=Problema%20ordine%20DapNow"
                data-testid="customer-contact-support-btn"
                className="btn-ghost w-full text-dapnow-muted"
              >
                Contatta supporto
              </a>
            )}
          </div>
        </div>
      </div>

      {cancelOpen && (
        <CancelOrderModal
          orderId={order.id}
          onClose={() => setCancelOpen(false)}
          onCancelled={(updated) => setOrder(updated)}
        />
      )}

      {order.status === "delivered" && order.delivery_photo_url && (
        <DeliveryPhotoView orderId={order.id} testid="customer-delivery-photo" />
      )}
      {/* Phase 44.10 — Preview foto ritiro lato customer rimossa.
          La foto resta visibile al driver per uso operativo. */}

      {order.status === "delivered" && (
        <div className="grid lg:grid-cols-2 gap-5">
          {/* Phase 44.07 — Inline form già renderizzato in alto sotto LiveActionCard.
              Qui resta solo la versione readonly o l'eventuale rating del driver. */}
          {ratings.find((r) => r.from_role === "customer") && (
            <div className="card-soft p-5 sm:p-6" data-testid="rating-readonly">
              <p className="overline">La tua valutazione</p>
              {ratings.filter((r) => r.from_role === "customer").map((r) => (
                <div key={r.id} className="mt-2">
                  <RatingDisplay avg={r.score} count={1} size="lg" />
                  {r.comment && <p className="mt-2 text-sm text-dapnow-muted">"{r.comment}"</p>}
                </div>
              ))}
            </div>
          )}
          {ratings.filter((r) => r.from_role === "driver").map((r) => (
            <div key={r.id} className="card-soft p-5 sm:p-6" data-testid="rating-from-driver">
              <p className="overline">Il driver ti ha valutato</p>
              <div className="mt-2"><RatingDisplay avg={r.score} count={1} size="lg" /></div>
              {r.comment && <p className="mt-2 text-sm text-dapnow-muted">"{r.comment}"</p>}
            </div>
          ))}
        </div>
      )}

      <ShareTrackingModal orderId={order.id} open={shareOpen} onClose={() => setShareOpen(false)} />
    </div>
  );
}

// Phase 43.18d — Card rimborso per ordini cancellati
function RefundStatusCard({ order }) {
  const rs = order.refund_status;
  const isPending = rs === "pending";
  const isSucceeded = rs === "succeeded";
  const isFailed = rs === "failed";
  const policy = order.cancellation_policy || {};
  const presentation = refundPresentation(order);
  const penalty = policy.penalty_chf ?? 0;

  const palette = isSucceeded
    ? "border-l-emerald-400 bg-emerald-50/40"
    : isFailed
      ? "border-l-amber-500 bg-amber-50/50"
      : "border-l-amber-400 bg-amber-50/40";
  const iconBg = isSucceeded ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700";

  return (
    <section className={`card-soft p-5 border-l-4 ${palette}`} data-testid="refund-status-card">
      <div className="flex items-start gap-3">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl flex-shrink-0 ${iconBg}`}>
          {isSucceeded ? <ShieldCheck className="h-5 w-5" /> : <Receipt className="h-5 w-5" />}
        </span>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-base font-bold tracking-tight">
            {presentation.title}
          </h3>
          <p className="text-sm text-dapnow-muted mt-0.5">
            {presentation.message}
          </p>
          {penalty > 0 && (
            <p className="text-xs text-dapnow-muted mt-1">
              Penale trattenuta in base alla policy di annullamento: <strong>{formatCHF(penalty)}</strong>.
            </p>
          )}
          {isSucceeded && order.refunded_at && (
            <p className="text-xs text-dapnow-muted mt-1">
              Aggiornato il {formatDateTime(order.refunded_at)}.
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
